import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Cpu, BookOpen, Shield, Search, GitPullRequest, Link, ChevronDown, ChevronRight, X, FlaskConical, Users } from 'lucide-react'
import { api } from '../api/client'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Tooltip } from '../components/ui/Tooltip'
import { PlaygroundPanel } from './Playground'
import type { CatalogItem } from '../api/types'

type Tab = 'agents' | 'skills' | 'rules' | 'playground' | 'contribute'

const TAB_KEYS: readonly Tab[] = ['agents', 'skills', 'rules', 'playground', 'contribute']

const CATALOG_DEFINITIONS: Partial<Record<Tab, string>> = {
  agents:
    'An Agent is an AI assistant set up for one specific job — like a teammate who is an expert at a single role. ' +
    'Example: the "MR Reviewer" agent. You ask it to review a pull request, and it takes on that reviewer persona — knowing what to look for, how to phrase feedback, and what verdict to give. ' +
    'An agent USES skills to do the work and FOLLOWS rules while doing it. ',
  skills:
    'A Skill is a reusable step-by-step "how-to" guide that teaches an agent the right way to do a task. ' +
    'Example: the "Code Review" skill walks the agent through a 5-step review — check security first, then correctness, then tests, then the API contract, then logging — and tells it to label each comment BLOCKING, SUGGEST, or NOTE. ' +
    'It is a PROCESS (the steps to follow). Many agents can borrow the same skill so reviews are always done the same way.',
  rules:
    'A Rule is a single standard or guardrail the AI must always obey — a team do or don\'t. ' +
    'Example: rule "No Secrets in Source Code" forbids hardcoded passwords or API keys and requires them to come from environment variables instead. ' +
    'It is a CONSTRAINT (a must / must-not), not a process. ' +
    'Skills point at the rules they enforce — the Code Review skill checks the mentioned rule during its security step.'
}

export function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams()
  // Honor ?tab= so deep links (e.g. the /playground redirect) land on the right tab.
  const initialTab = (TAB_KEYS as readonly string[]).includes(searchParams.get('tab') ?? '')
    ? (searchParams.get('tab') as Tab)
    : 'agents'
  const [tab, setTabState] = useState<Tab>(initialTab)
  const setTab = (next: Tab) => {
    setTabState(next)
    setSearchParams(next === 'agents' ? {} : { tab: next }, { replace: true })
  }
  const [search, setSearch] = useState('')
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set())
  const [detailItem, setDetailItem] = useState<CatalogItem | null>(null)

  const { data: catalog, isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.registry.getCatalog(),
  })

  const items: CatalogItem[] =
    tab === 'agents' || tab === 'skills' || tab === 'rules' ? (catalog?.[tab] ?? []) : []
  const filtered = items.filter(item =>
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.description.toLowerCase().includes(search.toLowerCase()) ||
    item.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  )

  // Build lookup maps for cross-referencing
  const skillsById = new Map<string, CatalogItem>()
  const agentsBySkill = new Map<string, string[]>()
  if (catalog) {
    for (const s of catalog.skills) skillsById.set(s.id, s)
    for (const a of catalog.agents) {
      for (const skillId of a.requires_skills ?? []) {
        const list = agentsBySkill.get(skillId) ?? []
        list.push(a.name || a.id)
        agentsBySkill.set(skillId, list)
      }
    }
  }

  function toggleAgent(id: string) {
    setExpandedAgents(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  type TabDef = { key: Tab; label: string; icon: React.ElementType; count: number }
  // Left group: the actual catalog (searchable item lists).
  const listTabs: TabDef[] = [
    { key: 'agents', label: 'Agents', icon: Cpu, count: catalog?.totals.agents ?? 0 },
    { key: 'skills', label: 'Skills', icon: BookOpen, count: catalog?.totals.skills ?? 0 },
    { key: 'rules', label: 'Rules', icon: Shield, count: catalog?.totals.rules ?? 0 },
  ]
  // Right group: tools/actions, kept visually separate from the catalog itself.
  const toolTabs: TabDef[] = [
    { key: 'playground', label: 'Playground', icon: FlaskConical, count: 0 },
    { key: 'contribute', label: 'Contribute', icon: GitPullRequest, count: 0 },
  ]

  const renderTab = (t: TabDef) => {
    const button = (
      <button
        onClick={() => setTab(t.key)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 'var(--radius-s)',
          border: 'none', cursor: 'pointer',
          background: tab === t.key ? 'var(--brand)' : 'var(--surface-hover)',
          color: tab === t.key ? '#fff' : 'var(--text-secondary)',
          fontWeight: 500, fontSize: 14, transition: 'all 0.15s',
        }}
      >
        <t.icon size={14} />
        {t.label}
        {t.count > 0 && (
          <span style={{
            background: tab === t.key ? 'rgba(255,255,255,0.2)' : 'var(--surface)',
            padding: '1px 6px', borderRadius: 10, fontSize: 11,
          }}>
            {t.count}
          </span>
        )}
      </button>
    )
    const definition = CATALOG_DEFINITIONS[t.key]
    if (!definition) return <React.Fragment key={t.key}>{button}</React.Fragment>
    return (
      <Tooltip key={t.key} id={`catalog-tab-tooltip-${t.key}`} content={definition}>
        {button}
      </Tooltip>
    )
  }

  // Tabs that render the searchable catalog item list.
  const isListTab = tab === 'agents' || tab === 'skills' || tab === 'rules'

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 'var(--space-3)' }}>AI Catalog</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
        Browse all agents, skills, and rules in the Context Lake — delivered to every developer IDE session via <code>get_startup_context()</code>.
      </p>

      {/* Tabs — catalog lists left-aligned, tools (Playground/Contribute) right-aligned */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {listTabs.map(renderTab)}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {toolTabs.map(renderTab)}
        </div>
      </div>

      {/* Search */}
      {isListTab && (
        <div style={{ position: 'relative', marginBottom: 'var(--space-3)' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 10, color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder={`Search ${tab}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-s)', color: 'var(--text-primary)',
              fontSize: 14, outline: 'none',
            }}
          />
        </div>
      )}

      {/* Playground tab — preview what a project/tags receive in the IDE */}
      {tab === 'playground' && <PlaygroundPanel />}

      {/* Contribute tab */}
      {tab === 'contribute' && <ContributeSection />}

      {/* Items */}
      {isListTab && (
        <CatalogContent
          isLoading={isLoading}
          filtered={filtered}
          search={search}
          tab={tab}
          setDetailItem={setDetailItem}
          expandedAgents={expandedAgents}
          toggleAgent={toggleAgent}
          skillsById={skillsById}
          agentsBySkill={agentsBySkill}
        />
      )}

      {/* Detail Modal */}
      {detailItem && (
        <DetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </div>
  )
}

// ─── Catalog Content (extracted to reduce cognitive complexity) ───────────────

function CatalogContent({ isLoading, filtered, search, tab, setDetailItem, expandedAgents, toggleAgent, skillsById, agentsBySkill }: Readonly<{
  isLoading: boolean
  filtered: CatalogItem[]
  search: string
  tab: string
  setDetailItem: (item: CatalogItem) => void
  expandedAgents: Set<string>
  toggleAgent: (id: string) => void
  skillsById: Map<string, CatalogItem>
  agentsBySkill: Map<string, string[]>
}>) {
  // Group items by team (managed_by) — computed before state so we can seed collapsed set
  const grouped = new Map<string, CatalogItem[]>()
  if (!isLoading) {
    for (const item of filtered) {
      const team = item.managed_by || 'platform'
      const list = grouped.get(team) ?? []
      list.push(item)
      grouped.set(team, list)
    }
  }
  const teamEntries = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]))

  // Default: all teams collapsed
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(() => new Set(teamEntries.map(([t]) => t)))

  function toggleTeam(team: string) {
    setCollapsedTeams(prev => {
      const next = new Set(prev)
      if (next.has(team)) next.delete(team)
      else next.add(team)
      return next
    })
  }

  if (isLoading) {
    return <div style={{ color: 'var(--text-muted)', padding: 'var(--space-3)' }}>Loading catalog…</div>
  }
  if (filtered.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-4)' }}>
          {search ? `No ${tab} matching "${search}"` : `No ${tab} available`}
        </div>
      </Card>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {teamEntries.map(([team, teamItems]) => (
        <div key={team}>
          <button
            onClick={() => toggleTeam(team)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
              color: 'var(--text-primary)', marginBottom: 'var(--space-1)',
            }}
          >
            {collapsedTeams.has(team)
              ? <ChevronRight size={14} color="var(--text-muted)" />
              : <ChevronDown size={14} color="var(--text-muted)" />}
            <Users size={14} color="var(--brand)" />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{team}</span>
            <span style={{
              fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-hover)',
              padding: '1px 7px', borderRadius: 10,
            }}>{teamItems.length}</span>
          </button>
          {!collapsedTeams.has(team) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', paddingLeft: 'var(--space-2)' }}>
              {teamItems.map(item => (
        <Card key={item.id} style={{ padding: 'var(--space-2) var(--space-3)', cursor: 'pointer', transition: 'border-color 0.15s' }} onClick={() => setDetailItem(item)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</span>
                {item.severity && <Badge variant={item.severity as 'mandatory' | 'recommended' | 'optional'}>{item.severity}</Badge>}
                {item.status && <Badge variant={item.status as 'published' | 'draft'}>{item.status}</Badge>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
                {item.description}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {item.tags.map(tag => (
                  <span key={tag} style={{
                    padding: '2px 8px', fontSize: 11, borderRadius: 4,
                    background: 'var(--surface-hover)', color: 'var(--text-muted)',
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
              {/* Skill dependencies for agents — expandable with descriptions */}
              {item.requires_skills && item.requires_skills.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button
                    onClick={() => toggleAgent(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', fontSize: 12, padding: 0,
                    }}
                  >
                    {expandedAgents.has(item.id)
                      ? <ChevronDown size={12} color="var(--success)" />
                      : <ChevronRight size={12} color="var(--success)" />}
                    <Link size={11} color="var(--success)" />
                    <span>requires {item.requires_skills.length} skill{item.requires_skills.length > 1 ? 's' : ''}</span>
                  </button>
                  {/* Collapsed: show skill IDs as pills */}
                  {!expandedAgents.has(item.id) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, marginLeft: 18 }}>
                      {item.requires_skills.map(s => (
                        <span key={s} style={{
                          padding: '2px 8px', fontSize: 11, borderRadius: 4,
                          background: 'rgba(34,197,94,0.1)', color: 'var(--success)',
                          border: '1px solid rgba(34,197,94,0.2)',
                        }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Expanded: show skill name + description */}
                  {expandedAgents.has(item.id) && (
                    <div style={{
                      marginTop: 8, marginLeft: 18, padding: '8px 12px',
                      background: 'var(--surface)', borderRadius: 'var(--radius-s)',
                      border: '1px solid var(--border)',
                    }}>
                      {item.requires_skills.map(skillId => {
                        const skill = skillsById.get(skillId)
                        return (
                          <div key={skillId} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '6px 0',
                            borderBottom: '1px solid var(--border)',
                          }}>
                            <BookOpen size={13} color="var(--success)" style={{ flexShrink: 0, marginTop: 2 }} />
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                                {skill?.name || skillId}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                {skill?.description || 'Skill not found in catalog'}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* Used by agents — shown on skills tab */}
              {tab === 'skills' && agentsBySkill.has(item.id) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <Cpu size={11} color="var(--brand)" />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>used by:</span>
                  {agentsBySkill.get(item.id)!.map(agentName => (
                    <span key={agentName} style={{
                      padding: '2px 8px', fontSize: 11, borderRadius: 4,
                      background: 'rgba(99,102,241,0.1)', color: 'var(--brand)',
                      border: '1px solid rgba(99,102,241,0.2)',
                    }}>
                      {agentName}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
              <div>{item.id}</div>
            </div>
          </div>
        </Card>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

type DetailTab = 'content' | 'skills' | 'rules'

function DetailModal({ item, onClose }: Readonly<{ item: CatalogItem; onClose: () => void }>) {
  const [activeTab, setActiveTab] = useState<DetailTab>('content')

  // Fetch agent detail (persona + linked skills/rules)
  const { data: agentDetail } = useQuery({
    queryKey: ['agent-detail', item.id],
    queryFn: () => api.registry.getAgent(item.id),
    enabled: item.type === 'agent',
    staleTime: 60_000,
  })

  // Fetch skill detail (content + linked rules)
  const { data: skillDetail } = useQuery({
    queryKey: ['skill-detail', item.id],
    queryFn: () => api.registry.getSkill(item.id),
    enabled: item.type === 'skill',
    staleTime: 60_000,
  })

  // Fetch rule detail (full content)
  const { data: ruleDetail } = useQuery({
    queryKey: ['rule-detail', item.id],
    queryFn: () => api.registry.getRule(item.id),
    enabled: item.type === 'rule',
    staleTime: 60_000,
  })

  // For agents: fetch each linked skill's content
  const linkedSkillIds = agentDetail?.uses_skills ?? item.requires_skills ?? []
  const { data: linkedSkillDetails } = useQuery({
    queryKey: ['linked-skills', linkedSkillIds.join(',')],
    queryFn: () => Promise.all(linkedSkillIds.map(id => api.registry.getSkill(id).catch(() => null))),
    enabled: item.type === 'agent' && linkedSkillIds.length > 0,
    staleTime: 60_000,
  })

  // Determine available tabs
  const contentLabel = { agent: 'Persona', skill: 'Workflow', rule: 'Rule' }[item.type] ?? 'Content'
  const contentIcon = { agent: Cpu, skill: BookOpen, rule: Shield }[item.type] ?? Shield
  const tabs: { key: DetailTab; label: string; icon: React.ElementType; show: boolean }[] = [
    { key: 'content', label: contentLabel, icon: contentIcon, show: true },
    { key: 'skills', label: `Skills (${linkedSkillIds.length})`, icon: BookOpen, show: item.type === 'agent' && linkedSkillIds.length > 0 },
    { key: 'rules', label: 'Rules', icon: Shield, show: (item.type === 'agent' && (agentDetail?.uses_rules?.length ?? 0) > 0) || (item.type === 'skill' && (skillDetail?.requires_rules?.length ?? 0) > 0) },
  ]

  const mainContentByType: Record<string, string | null | undefined> = {
    agent: agentDetail?.persona,
    skill: skillDetail?.content,
    rule: ruleDetail?.content,
  }
  const mainContent = mainContentByType[item.type] ?? null

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-m)',
          width: '100%', maxWidth: 800, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {item.type === 'agent' && <Cpu size={18} color="var(--brand)" />}
              {item.type === 'skill' && <BookOpen size={18} color="var(--success)" />}
              {item.type === 'rule' && <Shield size={18} color="var(--warning)" />}
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{item.name}</h2>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '2px 8px', borderRadius: 4 }}>{item.id}</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0 28px' }}>{item.description}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', paddingLeft: 'var(--space-4)' }}>
          {tabs.filter(t => t.show).map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: 13, fontWeight: 500,
                color: activeTab === t.key ? 'var(--brand)' : 'var(--text-muted)',
                borderBottom: activeTab === t.key ? '2px solid var(--brand)' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3) var(--space-4)' }}>
          {activeTab === 'content' && (
            <div>
              {!mainContent && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 'var(--space-3)' }}>
                  Loading content…
                </div>
              )}
              {mainContent && (
                <pre style={{
                  whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                  fontFamily: 'var(--font-mono, monospace)', fontSize: 13,
                  lineHeight: 1.6, color: 'var(--text-primary)',
                  background: 'var(--surface)', padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                  margin: 0,
                }}>
                  {mainContent}
                </pre>
              )}
              {/* Agent: show checklist if available */}
              {item.type === 'agent' && agentDetail?.has_checklist && agentDetail.checklist && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                    Startup Checklist
                  </div>
                  <pre style={{
                    whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                    fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
                    lineHeight: 1.5, color: 'var(--text-secondary)',
                    background: 'var(--surface-hover)', padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                    margin: 0,
                  }}>
                    {agentDetail.checklist}
                  </pre>
                </div>
              )}
            </div>
          )}

          {activeTab === 'skills' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {linkedSkillIds.map((skillId, idx) => {
                const detail = linkedSkillDetails?.[idx]
                return (
                  <div key={skillId} style={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-s)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '10px 14px', background: 'var(--surface)',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <BookOpen size={14} color="var(--success)" />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{detail?.title || skillId}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{skillId}</span>
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                      {detail ? (
                        <pre style={{
                          whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                          fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
                          lineHeight: 1.5, color: 'var(--text-secondary)',
                          margin: 0, maxHeight: 300, overflowY: 'auto',
                        }}>
                          {detail.content}
                        </pre>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {activeTab === 'rules' && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {item.type === 'agent' && agentDetail?.uses_rules && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {agentDetail.uses_rules.map(ruleId => (
                    <div key={ruleId} style={{
                      padding: '8px 12px', background: 'var(--surface)',
                      borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <Shield size={13} color="var(--warning)" />
                      <span style={{ fontWeight: 500 }}>{ruleId}</span>
                    </div>
                  ))}
                </div>
              )}
              {item.type === 'skill' && skillDetail?.requires_rules && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {skillDetail.requires_rules.map(ruleId => (
                    <div key={ruleId} style={{
                      padding: '8px 12px', background: 'var(--surface)',
                      borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <Shield size={13} color="var(--warning)" />
                      <span style={{ fontWeight: 500 }}>{ruleId}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Contribute Section ───────────────────────────────────────────────────────

function ContributeSection() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Card>
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Contribute to the Catalog</h3>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Any engineer can propose new rules, agents, or skills to the catalog through a Git merge request.
          </p>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
        {/* Add a Rule */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Shield size={18} color="var(--warning)" />
            <h4 style={{ fontSize: 14, fontWeight: 600 }}>Add a Rule</h4>
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Consult <code>sdlc-canonical/index.yaml</code> and create the file in its declared pack path (currently <code>@kone/global</code> uses <code>sdlc-canonical/rules/global</code>)</li>
            <li>Name the Markdown file so it begins with its frontmatter ID (for example, <code>GLB-001-minimal-change-policy.instructions.md</code>)</li>
            <li>Add required YAML frontmatter: <code>id</code>, <code>domain</code>, <code>severity</code>, and <code>status</code>; <code>title</code> and <code>profiles</code> are optional metadata</li>
            <li>Use allowed domain values (<code>security</code>, <code>development</code>, <code>testing</code>, <code>operations</code>, or <code>design</code>), severity values (<code>mandatory</code>, <code>recommended</code>, or <code>optional</code>), and status values (<code>published</code>, <code>draft</code>, or <code>deprecated</code>)</li>
            <li>Write the rule body with clear instructions</li>
            <li>Open a Git merge request; after merge, canonical content is synced for MCP consumption</li>
          </ol>
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>Rule filenames start with their frontmatter ID.</strong>
          </div>
        </Card>

        {/* Add an Agent */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Cpu size={18} color="var(--brand)" />
            <h4 style={{ fontSize: 14, fontWeight: 600 }}>Add an Agent</h4>
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Create a folder in <code>sdlc-canonical/agents/&lt;agent-id&gt;/</code></li>
            <li>Add <code>manifest.yaml</code> (name, description, tags)</li>
            <li>Add <code>persona.md</code> with YAML frontmatter + persona text</li>
            <li>Optionally add <code>checklist.md</code> for startup sequence</li>
            <li>Register in <code>index.yaml</code> and open a merge request</li>
          </ol>
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>Agents are delivered to all projects</strong> — no profile filtering.
          </div>
        </Card>

        {/* Add a Skill */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BookOpen size={18} color="var(--success)" />
            <h4 style={{ fontSize: 14, fontWeight: 600 }}>Add a Skill</h4>
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Create a folder in <code>sdlc-canonical/skills/SKL-NNN-name/</code></li>
            <li>Add <code>manifest.yaml</code> (name, description, tags)</li>
            <li>Add <code>skill.md</code> with step-by-step workflow (use ### headings)</li>
            <li>Declare <code>requires_rules</code> and <code>requires_agents</code> in frontmatter</li>
            <li>Register in <code>index.yaml</code> and open a merge request</li>
          </ol>
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>Skills are step-by-step workflows</strong> the AI executes in order.
          </div>
        </Card>
      </div>

      <Card>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          <strong>Contribution review:</strong> Submit changes in a Git merge request. For canonical rules,
          after merge the canonical content is synced for MCP consumption.
        </div>
      </Card>
    </div>
  )
}
